/**
 * IT Dashboard Asset Modal Components
 * Modal dialogs for IT Asset Management
 */
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateOnly } from '../../utils/formatters';

// Slug → legacy category mapping (keeps category-based stats/reports working)
const slugToCategory = (slug = '') => {
  if (slug.includes('laptop')) return 'laptop';
  if (slug.includes('cpu') || slug.includes('desktop')) return 'desktop';
  if (slug.includes('monitor')) return 'monitor';
  if (slug.includes('keyboard')) return 'keyboard';
  if (slug.includes('mouse')) return 'mouse';
  if (slug.includes('headset')) return 'headset';
  if (slug.includes('phone') || slug.includes('mobile') || slug.includes('polycom') || slug.includes('epabx')) return 'phone';
  if (slug.includes('printer')) return 'printer';
  if (slug.includes('router') || slug.includes('switch') || slug.includes('firewall') || slug.includes('access_point') || slug.includes('wifi') || slug.includes('voip') || slug.includes('dvr')) return 'network';
  if (slug.includes('pen_drive')) return 'storage';
  return 'peripheral';
};

// Renders one dynamic input from a product-type field schema entry
const CustomFieldInput = ({ field, value, onChange }) => {
  const common = {
    value: value ?? '',
    onChange: e => onChange(e.target.value),
    required: !!field.required,
    'data-testid': `custom-field-${field.key}`,
  };
  if (field.type === 'select') {
    return (
      <select {...common}>
        <option value="">— select —</option>
        {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (field.type === 'boolean') {
    return (
      <select {...common}>
        <option value="">— select —</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    );
  }
  const inputType = field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text';
  return <input type={inputType} placeholder={field.label} {...common} />;
};

// ASSET-OWNER-HR-001 — Owner picker: type-to-search the HR Employee Directory
// (active employees only, scoped endpoint). Selecting links the asset to the
// employee record; plain typed text stays a legacy free-text owner.
const OwnerPicker = ({ ownerText, ownerEmployeeId, onChange, testId }) => {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const search = async (q) => {
    onChange({ text: q, employeeId: null });
    if (q.trim().length < 2) { setResults([]); setOpen(false); return; }
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/it-assets/employee-directory?q=${encodeURIComponent(q.trim())}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const data = await res.json();
      setResults(data.employees || []);
      setOpen(true);
    } catch (e) { setResults([]); }
  };
  return (
    <div style={{ position: 'relative' }}>
      <input type="text" value={ownerText}
        onChange={e => search(e.target.value)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Search HR employee… (or type free text)"
        data-testid={testId} autoComplete="off" />
      {ownerEmployeeId && (
        <span data-testid={`${testId}-linked-badge`} style={{
          position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
          fontSize: 11, color: '#34d399', pointerEvents: 'none' }}>✓ HR-linked</span>
      )}
      {open && results.length > 0 && (
        <div data-testid={`${testId}-dropdown`} style={{
          position: 'absolute', zIndex: 50, top: '100%', left: 0, right: 0, marginTop: 4,
          background: '#141a33', border: '1px solid #2c3558', borderRadius: 8, color: '#e2e8f0',
          maxHeight: 220, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,.45)' }}>
          {results.map(emp => (
            <div key={emp.employee_id} data-testid={`owner-option-${emp.employee_id}`}
              onMouseDown={() => { onChange({ text: emp.full_name, employeeId: emp.employee_id }); setOpen(false); }}
              style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex',
                       justifyContent: 'space-between', gap: 8 }}
              onMouseEnter={e => { e.currentTarget.style.background = '#1d2547'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
              <span>{emp.full_name}</span>
              <span style={{ opacity: .55, fontSize: 12 }}>{emp.employee_id}{emp.team ? ` · ${emp.team}` : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Add Asset Modal
export const AddAssetModal = ({ isOpen, onClose, onSubmit, assetTypes = [] }) => {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('laptop');
  const [assetTypeId, setAssetTypeId] = useState('');
  const [customFields, setCustomFields] = useState({});
  const [risk, setRisk] = useState('');
  const [owner, setOwner] = useState('');
  const [ownerEmployeeId, setOwnerEmployeeId] = useState(null);
  const [legacyDeviceNumber, setLegacyDeviceNumber] = useState('');
  const [stockState, setStockState] = useState('working');
  const [nonWorkingReason, setNonWorkingReason] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [model, setModel] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [purchaseCost, setPurchaseCost] = useState('');
  const [warrantyExpiry, setWarrantyExpiry] = useState('');
  const [location, setLocation] = useState('');
  const [condition, setCondition] = useState('new');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedType = assetTypes.find(t => t.id === assetTypeId) || null;

  const handleTypeChange = (id) => {
    setAssetTypeId(id);
    setCustomFields({});
    const t = assetTypes.find(x => x.id === id);
    if (t) setCategory(slugToCategory(t.slug));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (stockState === 'non_working' && !nonWorkingReason.trim()) {
      toast.error('A reason is required when stock is Non-Working');
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit({
        name,
        category,
        asset_type_id: assetTypeId || null,
        custom_fields: customFields,
        risk: risk || null,
        owner: owner || null,
        owner_employee_id: ownerEmployeeId || null,
        legacy_device_number: legacyDeviceNumber || null,
        stock_state: stockState,
        non_working_reason: stockState === 'non_working' ? nonWorkingReason : null,
        serial_number: serialNumber || null,
        model: model || null,
        manufacturer: manufacturer || null,
        purchase_date: purchaseDate || null,
        purchase_cost: purchaseCost ? parseFloat(purchaseCost) : null,
        warranty_expiry: warrantyExpiry || null,
        location: location || null,
        condition,
        notes: notes || null
      });
      // Reset form
      setName(''); setCategory('laptop'); setSerialNumber(''); setModel('');
      setManufacturer(''); setPurchaseDate(''); setPurchaseCost('');
      setWarrantyExpiry(''); setLocation(''); setCondition('new'); setNotes('');
      setAssetTypeId(''); setCustomFields({}); setRisk(''); setOwner(''); setOwnerEmployeeId(null);
      setLegacyDeviceNumber(''); setStockState('working'); setNonWorkingReason('');
      onClose();
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay avm-overlay" onClick={onClose}>
      <div className="avm-modal" onClick={e => e.stopPropagation()} data-testid="add-asset-modal">
        {/* Fixed header */}
        <div className="avm-header">
          <div className="avm-header-left">
            <div className="avm-header-icon">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><rect x="8" y="8" width="8" height="8" rx="1"/></svg>
            </div>
            <div>
              <h3>Add New IT Asset</h3>
              <p>Register and track hardware, equipment and technology assets</p>
            </div>
          </div>
          <button type="button" className="avm-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>

        <form onSubmit={handleSubmit} className="avm-form">
          {/* Scrollable body */}
          <div className="avm-body">
            {/* ASSET INFORMATION */}
            <div className="avm-section">
              <div className="avm-section-title">▣ Asset Information</div>
              <div className="avm-grid-3">
                <div className="avm-field">
                  <label>Asset Name <span className="avm-req">*</span></label>
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="e.g., Dell Latitude 5540"
                    required
                    data-testid="asset-name-input"
                  />
                </div>
                <div className="avm-field">
                  <label>Product Type <span className="avm-req">*</span></label>
                  {assetTypes.length > 0 ? (
                    <select value={assetTypeId} onChange={e => handleTypeChange(e.target.value)}
                      required data-testid="asset-product-type-select">
                      <option value="">— select product type —</option>
                      {assetTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  ) : (
                    <select value={category} onChange={e => setCategory(e.target.value)}>
                      <option value="laptop">Laptop</option>
                      <option value="desktop">Desktop</option>
                      <option value="monitor">Monitor</option>
                      <option value="keyboard">Keyboard</option>
                      <option value="mouse">Mouse</option>
                      <option value="headset">Headset</option>
                      <option value="phone">Phone</option>
                      <option value="tablet">Tablet</option>
                      <option value="printer">Printer</option>
                      <option value="network">Network Equipment</option>
                      <option value="server">Server</option>
                      <option value="storage">Storage</option>
                      <option value="peripheral">Peripheral</option>
                      <option value="software">Software License</option>
                      <option value="other">Other</option>
                    </select>
                  )}
                </div>
                <div className="avm-field">
                  <label>Device Number (legacy)</label>
                  <input type="text" value={legacyDeviceNumber}
                    onChange={e => setLegacyDeviceNumber(e.target.value)}
                    placeholder="Existing physical device number"
                    data-testid="asset-legacy-device-number" />
                </div>
                <div className="avm-field">
                  <label>Manufacturer</label>
                  <input type="text" value={manufacturer} onChange={e => setManufacturer(e.target.value)}
                    placeholder="e.g., Dell, HP, Lenovo" />
                </div>
                <div className="avm-field">
                  <label>Model</label>
                  <input type="text" value={model} onChange={e => setModel(e.target.value)}
                    placeholder="Model number" />
                </div>
                <div className="avm-field">
                  <label>Serial Number</label>
                  <input type="text" value={serialNumber} onChange={e => setSerialNumber(e.target.value)}
                    placeholder="S/N or Service Tag" />
                </div>
              </div>
              {/* Dynamic fields from the selected product type's schema */}
              {selectedType && (selectedType.field_schema || []).length > 0 && (
                <div className="avm-grid-3 avm-custom-fields">
                  {selectedType.field_schema.map(f => (
                    <div className="avm-field" key={f.key}>
                      <label>{f.label}{f.required ? ' *' : ''}</label>
                      <CustomFieldInput
                        field={f}
                        value={customFields[f.key]}
                        onChange={v => setCustomFields(cf => ({ ...cf, [f.key]: v }))}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* OWNERSHIP & LOCATION */}
            <div className="avm-section">
              <div className="avm-section-title">⌖ Ownership &amp; Location</div>
              <div className="avm-grid-3">
                <div className="avm-field">
                  <label>Owner</label>
                  <OwnerPicker ownerText={owner} ownerEmployeeId={ownerEmployeeId}
                    onChange={({ text, employeeId }) => { setOwner(text); setOwnerEmployeeId(employeeId); }}
                    testId="asset-owner-input" />
                </div>
                <div className="avm-field">
                  <label>Location</label>
                  <input type="text" value={location} onChange={e => setLocation(e.target.value)}
                    placeholder="e.g., IT Storage Room A" />
                </div>
                <div className="avm-field">
                  <label>Stock State</label>
                  <select value={stockState} onChange={e => setStockState(e.target.value)}
                    className={stockState === 'non_working' ? 'avm-val-danger' : 'avm-val-ok'}
                    data-testid="asset-stock-state-select">
                    <option value="working">Working</option>
                    <option value="non_working">Non-Working</option>
                  </select>
                </div>
              </div>
              {stockState === 'non_working' && (
                <div className="avm-field avm-full">
                  <label>Non-Working Reason <span className="avm-req">*</span></label>
                  <textarea value={nonWorkingReason} onChange={e => setNonWorkingReason(e.target.value)}
                    placeholder="Why is this device not working?" rows={2} required
                    data-testid="asset-non-working-reason" />
                </div>
              )}
            </div>

            {/* CONDITION & RISK + PURCHASE & WARRANTY side by side */}
            <div className="avm-side-cards">
              <div className="avm-section">
                <div className="avm-section-title">◇ Condition &amp; Risk</div>
                <div className="avm-grid-2">
                  <div className="avm-field">
                    <label>Condition</label>
                    <select value={condition} onChange={e => setCondition(e.target.value)}
                      className={`avm-cond-${condition}`}>
                      <option value="new">New</option>
                      <option value="excellent">Excellent</option>
                      <option value="good">Good</option>
                      <option value="fair">Fair</option>
                      <option value="poor">Poor</option>
                    </select>
                  </div>
                  <div className="avm-field">
                    <label>Risk</label>
                    <select value={risk} onChange={e => setRisk(e.target.value)}
                      className={risk ? `avm-risk-${risk}` : ''}
                      data-testid="asset-risk-select">
                      <option value="">— not set —</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="avm-section">
                <div className="avm-section-title">$ Purchase &amp; Warranty</div>
                <div className="avm-grid-3">
                  <div className="avm-field">
                    <label>Purchase Date</label>
                    <input type="date" value={purchaseDate} onChange={e => setPurchaseDate(e.target.value)} />
                  </div>
                  <div className="avm-field">
                    <label>Purchase Cost ($)</label>
                    <input type="number" step="0.01" value={purchaseCost}
                      onChange={e => setPurchaseCost(e.target.value)} placeholder="0.00" />
                  </div>
                  <div className="avm-field">
                    <label>Warranty Expiry</label>
                    <input type="date" value={warrantyExpiry} onChange={e => setWarrantyExpiry(e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            {/* NOTES */}
            <div className="avm-section">
              <div className="avm-section-title">≡ Notes</div>
              <div className="avm-field avm-full">
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Additional information..."
                  rows={2}
                />
              </div>
            </div>
          </div>

          {/* Sticky footer */}
          <div className="avm-footer">
            <button type="button" onClick={onClose} className="avm-btn-cancel">Cancel</button>
            <button type="submit" className="avm-btn-save" disabled={isSubmitting || !name} data-testid="asset-create-btn">
              {isSubmitting ? 'Creating...' : 'Add Asset'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Asset Modal (Batch 2) — standard + custom fields, pre-filled
const buildEditForm = (a = {}) => ({
  name: a.name || '',
  asset_type_id: a.asset_type_id || '',
  risk: a.risk || '',
  owner: a.owner || '',
  owner_employee_id: a.owner_employee_id || null,
  legacy_device_number: a.legacy_device_number || '',
  stock_state: a.stock_state || 'working',
  non_working_reason: a.non_working_reason || '',
  serial_number: a.serial_number || '',
  model: a.model || '',
  manufacturer: a.manufacturer || '',
  purchase_cost: a.purchase_cost ?? '',
  purchase_date: a.purchase_date || '',
  warranty_expiry: a.warranty_expiry || '',
  location: a.location || '',
  condition: a.condition || 'good',
  notes: a.notes || '',
});

export const EditAssetModal = ({ isOpen, onClose, onSubmit, asset, assetTypes = [] }) => {
  const [form, setForm] = useState(() => buildEditForm(asset || {}));
  const [customFields, setCustomFields] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ownerEmp, setOwnerEmp] = useState(null);

  useEffect(() => {
    if (asset && isOpen) {
      setForm(buildEditForm(asset));
      setCustomFields({ ...(asset.custom_fields || {}) });
    }
  }, [asset, isOpen]);

  // HR-linked owner details card (name / ID / department / designation)
  useEffect(() => {
    const id = form.owner_employee_id;
    if (!isOpen || !id) { setOwnerEmp(null); return; }
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/it-assets/employee-directory?q=${encodeURIComponent(id)}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        const data = await res.json();
        setOwnerEmp((data.employees || []).find(e => e.employee_id === id) || null);
      } catch (e) { setOwnerEmp(null); }
    })();
  }, [form.owner_employee_id, isOpen]);

  if (!isOpen || !asset) return null;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const selectedType = assetTypes.find(t => t.id === form.asset_type_id) || null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.stock_state === 'non_working' && !String(form.non_working_reason || '').trim()) {
      toast.error('A reason is required when stock is Non-Working');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        name: form.name,
        asset_type_id: form.asset_type_id || null,
        custom_fields: customFields,
        risk: form.risk || null,
        owner: form.owner || null,
        owner_employee_id: form.owner_employee_id || null,
        legacy_device_number: form.legacy_device_number || null,
        stock_state: form.stock_state,
        non_working_reason: form.stock_state === 'non_working' ? form.non_working_reason : null,
        serial_number: form.serial_number || null,
        model: form.model || null,
        manufacturer: form.manufacturer || null,
        purchase_cost: form.purchase_cost !== '' ? parseFloat(form.purchase_cost) : null,
        purchase_date: form.purchase_date || null,
        warranty_expiry: form.warranty_expiry || null,
        location: form.location || null,
        condition: form.condition,
        notes: form.notes || null,
      };
      if (selectedType && selectedType.id !== asset.asset_type_id) {
        payload.category = slugToCategory(selectedType.slug);
      }
      await onSubmit(asset.id, payload);
      onClose();
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay avm-overlay" onClick={onClose}>
      <div className="avm-modal" onClick={e => e.stopPropagation()} data-testid="edit-asset-modal">
        <div className="avm-header">
          <div className="avm-header-left">
            <div className="avm-header-icon">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
            </div>
            <div>
              <h3>Edit Asset — {asset.asset_tag}</h3>
              <p>Update hardware, ownership and lifecycle details</p>
            </div>
          </div>
          <button type="button" className="avm-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>

        <form onSubmit={handleSubmit} className="avm-form">
          <div className="avm-body">
            {/* ASSET INFORMATION */}
            <div className="avm-section">
              <div className="avm-section-title">▣ Asset Information</div>
              <div className="avm-grid-3">
                <div className="avm-field">
                  <label>Asset Name <span className="avm-req">*</span></label>
                  <input type="text" value={form.name} onChange={e => set('name', e.target.value)} required
                    data-testid="edit-asset-name" />
                </div>
                <div className="avm-field">
                  <label>Product Type</label>
                  <select value={form.asset_type_id} onChange={e => set('asset_type_id', e.target.value)}
                    data-testid="edit-asset-product-type">
                    <option value="">— unchanged / none —</option>
                    {assetTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div className="avm-field">
                  <label>Device Number (legacy)</label>
                  <input type="text" value={form.legacy_device_number}
                    onChange={e => set('legacy_device_number', e.target.value)}
                    placeholder="Existing physical device number"
                    data-testid="edit-asset-device-number" />
                </div>
                <div className="avm-field">
                  <label>Manufacturer</label>
                  <input type="text" value={form.manufacturer} onChange={e => set('manufacturer', e.target.value)}
                    placeholder="e.g., Dell, HP, Lenovo" />
                </div>
                <div className="avm-field">
                  <label>Model</label>
                  <input type="text" value={form.model} onChange={e => set('model', e.target.value)}
                    placeholder="Model number" />
                </div>
                <div className="avm-field">
                  <label>Serial Number</label>
                  <input type="text" value={form.serial_number} onChange={e => set('serial_number', e.target.value)}
                    placeholder="S/N or Service Tag" />
                </div>
              </div>
              {selectedType && (selectedType.field_schema || []).length > 0 && (
                <div className="avm-grid-3 avm-custom-fields">
                  {selectedType.field_schema.map(f => (
                    <div className="avm-field" key={f.key}>
                      <label>{f.label}{f.required ? ' *' : ''}</label>
                      <CustomFieldInput field={f} value={customFields[f.key]}
                        onChange={v => setCustomFields(cf => ({ ...cf, [f.key]: v }))} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* OWNERSHIP & LOCATION */}
            <div className="avm-section">
              <div className="avm-section-title">⌖ Ownership &amp; Location</div>
              <div className="avm-grid-3">
                <div className="avm-field">
                  <label>Owner</label>
                  <OwnerPicker ownerText={form.owner} ownerEmployeeId={form.owner_employee_id}
                    onChange={({ text, employeeId }) => setForm(p => ({ ...p, owner: text, owner_employee_id: employeeId }))}
                    testId="edit-asset-owner" />
                </div>
                <div className="avm-field">
                  <label>Location</label>
                  <input type="text" value={form.location} onChange={e => set('location', e.target.value)}
                    placeholder="e.g., IT Storage Room A" />
                </div>
                <div className="avm-field">
                  <label>Stock State</label>
                  <select value={form.stock_state} onChange={e => set('stock_state', e.target.value)}
                    className={form.stock_state === 'non_working' ? 'avm-val-danger' : 'avm-val-ok'}
                    data-testid="edit-asset-stock-state">
                    <option value="working">Working</option>
                    <option value="non_working">Non-Working</option>
                  </select>
                </div>
              </div>
              {ownerEmp && (
                <div className="avm-owner-card" data-testid="edit-asset-owner-details">
                  <div className="avm-owner-card-main">
                    <span className="avm-owner-name">{ownerEmp.full_name}</span>
                    <span className="avm-owner-tag">HR-linked</span>
                  </div>
                  <div className="avm-owner-card-meta">
                    <span>{ownerEmp.employee_id}</span>
                    {ownerEmp.team && <span>· {ownerEmp.team}</span>}
                    {ownerEmp.designation && <span>· {ownerEmp.designation}</span>}
                  </div>
                </div>
              )}
              {form.stock_state === 'non_working' && (
                <div className="avm-field avm-full">
                  <label>Non-Working Reason <span className="avm-req">*</span></label>
                  <textarea value={form.non_working_reason} onChange={e => set('non_working_reason', e.target.value)}
                    placeholder="Why is this device not working?" rows={2} required
                    data-testid="edit-asset-non-working-reason" />
                </div>
              )}
            </div>

            {/* CONDITION & RISK + PURCHASE & WARRANTY */}
            <div className="avm-side-cards">
              <div className="avm-section">
                <div className="avm-section-title">◇ Condition &amp; Risk</div>
                <div className="avm-grid-2">
                  <div className="avm-field">
                    <label>Condition</label>
                    <select value={form.condition} onChange={e => set('condition', e.target.value)}
                      className={`avm-cond-${form.condition}`}>
                      <option value="new">New</option>
                      <option value="excellent">Excellent</option>
                      <option value="good">Good</option>
                      <option value="fair">Fair</option>
                      <option value="poor">Poor</option>
                      <option value="damaged">Damaged</option>
                    </select>
                  </div>
                  <div className="avm-field">
                    <label>Risk</label>
                    <select value={form.risk} onChange={e => set('risk', e.target.value)}
                      className={form.risk ? `avm-risk-${form.risk}` : ''}
                      data-testid="edit-asset-risk">
                      <option value="">— not set —</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="avm-section">
                <div className="avm-section-title">$ Purchase &amp; Warranty</div>
                <div className="avm-grid-3">
                  <div className="avm-field">
                    <label>Purchase Date</label>
                    <input type="date" value={form.purchase_date} onChange={e => set('purchase_date', e.target.value)}
                      data-testid="edit-asset-purchase-date" />
                  </div>
                  <div className="avm-field">
                    <label>Purchase Cost ($)</label>
                    <input type="number" step="0.01" value={form.purchase_cost}
                      onChange={e => set('purchase_cost', e.target.value)} placeholder="0.00" />
                  </div>
                  <div className="avm-field">
                    <label>Warranty Expiry</label>
                    <input type="date" value={form.warranty_expiry} onChange={e => set('warranty_expiry', e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            {/* NOTES */}
            <div className="avm-section">
              <div className="avm-section-title">≡ Notes</div>
              <div className="avm-field avm-full">
                <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
                  placeholder="Additional information..." rows={2} />
              </div>
            </div>
          </div>

          <div className="avm-footer">
            <button type="button" onClick={onClose} className="avm-btn-cancel">Cancel</button>
            <button type="submit" className="avm-btn-save" disabled={isSubmitting || !form.name} data-testid="edit-asset-save-btn">
              {isSubmitting ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Assign Asset Modal
export const AssignAssetModal = ({ isOpen, onClose, onSubmit, asset, users }) => {
  const [userId, setUserId] = useState('');
  const [condition, setCondition] = useState('good');
  const [notes, setNotes] = useState('');
  const [expectedReturnDate, setExpectedReturnDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit(asset.id, {
        user_id: userId,
        condition_at_handover: condition,
        notes: notes || null,
        expected_return_date: expectedReturnDate || null
      });
      setUserId(''); setCondition('good'); setNotes(''); setExpectedReturnDate('');
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!isOpen || !asset) return null;
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Assign Asset</h3>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="asset-info-summary">
          <strong>{asset.name}</strong>
          <span className="asset-tag-small">{asset.asset_tag}</span>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Assign To *</label>
            <select value={userId} onChange={e => setUserId(e.target.value)} required>
              <option value="">Select User...</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>
                  {u.full_name || u.email} ({u.role})
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Condition at Handover *</label>
              <select value={condition} onChange={e => setCondition(e.target.value)}>
                <option value="new">New</option>
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
              </select>
            </div>
            <div className="form-group">
              <label>Expected Return Date</label>
              <input
                type="date"
                value={expectedReturnDate}
                onChange={e => setExpectedReturnDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Assignment notes..."
              rows={2}
            />
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary" disabled={isSubmitting || !userId}>
              {isSubmitting ? 'Assigning...' : 'Assign Asset'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Asset Detail Modal
export const AssetDetailModal = ({ isOpen, onClose, asset }) => {
  if (!isOpen || !asset) return null;
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-row">
            <h3>Asset Details</h3>
            <span className="asset-tag-badge">{asset.asset_tag}</span>
          </div>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body asset-detail-body">
          {/* QR Code */}
          {asset.qr_code && (
            <div className="qr-code-section">
              <img 
                src={`data:image/png;base64,${asset.qr_code}`} 
                alt="Asset QR Code"
                className="qr-code-image"
              />
              <span className="qr-label">Scan for quick lookup</span>
            </div>
          )}
          
          {/* Asset Info */}
          <div className="asset-info-grid">
            <div className="info-item">
              <span className="info-label">Name</span>
              <span className="info-value">{asset.name}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Category</span>
              <span className="info-value">{asset.category}</span>
            </div>
            {asset.asset_type_name && (
              <div className="info-item">
                <span className="info-label">Product Type</span>
                <span className="info-value">{asset.asset_type_name}</span>
              </div>
            )}
            {asset.legacy_device_number && (
              <div className="info-item">
                <span className="info-label">Device Number</span>
                <span className="info-value mono">{asset.legacy_device_number}</span>
              </div>
            )}
            {asset.risk && (
              <div className="info-item">
                <span className="info-label">Risk</span>
                <span className={`status-badge risk-${asset.risk}`}>{asset.risk}</span>
              </div>
            )}
            {asset.owner && (
              <div className="info-item">
                <span className="info-label">Owner</span>
                <span className="info-value">{asset.owner}</span>
              </div>
            )}
            {asset.stock_state && (
              <div className="info-item">
                <span className="info-label">Stock State</span>
                <span className="info-value">
                  {asset.stock_state === 'non_working' ? 'Non-Working' : 'Working'}
                  {asset.non_working_reason ? ` — ${asset.non_working_reason}` : ''}
                </span>
              </div>
            )}
            {Object.entries(asset.custom_fields || {})
              .filter(([, v]) => v !== null && v !== '')
              .map(([k, v]) => (
                <div className="info-item" key={k}>
                  <span className="info-label">{k.replace(/_/g, ' ')}</span>
                  <span className="info-value">{String(v)}</span>
                </div>
              ))}
            <div className="info-item">
              <span className="info-label">Status</span>
              <span className={`status-badge status-${asset.status}`}>{asset.status?.replace('_', ' ')}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Condition</span>
              <span className={`condition-badge cond-${asset.condition}`}>{asset.condition}</span>
            </div>
            {asset.serial_number && (
              <div className="info-item">
                <span className="info-label">Serial Number</span>
                <span className="info-value mono">{asset.serial_number}</span>
              </div>
            )}
            {asset.model && (
              <div className="info-item">
                <span className="info-label">Model</span>
                <span className="info-value">{asset.model}</span>
              </div>
            )}
            {asset.manufacturer && (
              <div className="info-item">
                <span className="info-label">Manufacturer</span>
                <span className="info-value">{asset.manufacturer}</span>
              </div>
            )}
            {asset.location && (
              <div className="info-item">
                <span className="info-label">Location</span>
                <span className="info-value">{asset.location}</span>
              </div>
            )}
            {asset.purchase_date && (
              <div className="info-item">
                <span className="info-label">Purchase Date</span>
                <span className="info-value">{asset.purchase_date}</span>
              </div>
            )}
            {asset.purchase_cost && (
              <div className="info-item">
                <span className="info-label">Purchase Cost</span>
                <span className="info-value">${asset.purchase_cost?.toFixed(2)}</span>
              </div>
            )}
            {asset.warranty_expiry && (
              <div className="info-item">
                <span className="info-label">Warranty Expiry</span>
                <span className="info-value">{asset.warranty_expiry}</span>
              </div>
            )}
            {asset.assigned_to_name && (
              <div className="info-item full-width">
                <span className="info-label">Currently Assigned To</span>
                <span className="info-value">{asset.assigned_to_name}</span>
              </div>
            )}
          </div>
          
          {/* Assignment History */}
          {asset.assignment_history?.length > 0 && (
            <div className="history-section">
              <h4>Assignment History</h4>
              <div className="history-list">
                {asset.assignment_history.map((h, i) => (
                  <div key={i} className="history-item">
                    <div className="history-main">
                      <span className="history-user">{h.user_name}</span>
                      <span className="history-date">{formatDateOnly(h.assigned_at)}</span>
                    </div>
                    <div className="history-details">
                      <span>Condition: {h.condition_at_handover}</span>
                      {h.returned_at && <span>• Returned: {formatDateOnly(h.returned_at)}</span>}
                      {h.acknowledged && <span className="ack-badge">✓ Acknowledged</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Maintenance History */}
          {asset.maintenance_history?.length > 0 && (
            <div className="history-section">
              <h4>Maintenance History</h4>
              <div className="history-list">
                {asset.maintenance_history.map((m, i) => (
                  <div key={i} className="history-item">
                    <div className="history-main">
                      <span className="history-type">{m.maintenance_type}</span>
                      <span className={`maint-status status-${m.status}`}>{m.status}</span>
                    </div>
                    <div className="history-details">
                      <span>{m.description}</span>
                      {m.vendor && <span>• Vendor: {m.vendor}</span>}
                      {m.actual_cost && <span>• Cost: ${m.actual_cost}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
